import React, { useMemo, useState } from 'react';

import { Permission } from '@admin/components/helpers/permission';
import { Spinner } from '@admin/components/spinner';
import { usePermission } from '@admin/hooks/use_permission';
import { TEAM } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import { useAdminTeamsQuery } from '@admin/schema';
import { TeamManagementSummaryFragment } from '@admin/schema';

import { MembersModal } from './members_modal';
import { DEFAULT_TEAM_SORT, TeamSort, TeamSortKey, sortTeams, teamSortLabel } from './sort_teams';
import {
  ActionGroup,
  ActionsCellContent,
  buildTeamMeta,
  Card,
  CardBody,
  CellContent,
  CompactCellContent,
  EmptyState,
  MetaLine,
  Page,
  PageHeader,
  PageSubtitle,
  PageTitle,
  Pill,
  PillGroup,
  PrimaryButton,
  SortBar,
  SortChip,
  SortLabel,
  Table,
  TableWrap,
  TeamName,
  TextButton,
} from './teams_styles';
import { UpsertModal } from './upsert_modal';

const SORT_KEYS: TeamSortKey[] = ['name', 'geo', 'region', 'members', 'assignable'];

export const TeamsList: React.FC = () => {
  const canManageTeams = usePermission(TEAM.resource, TEAM.actions.update);
  const [showUpsertModal, setShowUpsertModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState<TeamManagementSummaryFragment | undefined>(undefined);
  const [viewingMembersTeam, setViewingMembersTeam] = useState<TeamManagementSummaryFragment | undefined>(undefined);
  const [sort, setSort] = useState<TeamSort>(DEFAULT_TEAM_SORT);

  const { data, loading, refetch } = useAdminTeamsQuery({
    client,
    skip: !canManageTeams,
  });

  const sortedTeams = useMemo(
    () => (data?.adminTeams ? sortTeams(data.adminTeams, sort) : []),
    [data?.adminTeams, sort],
  );

  const toggleSort = (key: TeamSortKey) => {
    setSort((current) => {
      if (current.key === key) {
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
      }

      return { key, direction: 'asc' };
    });
  };

  const sortArrow = (key: TeamSortKey) => {
    if (sort.key !== key) {
      return '';
    }

    return sort.direction === 'asc' ? ' ↑' : ' ↓';
  };

  const openCreateModal = () => {
    setEditingTeam(undefined);
    setShowUpsertModal(true);
  };

  const openEditModal = (team: TeamManagementSummaryFragment) => {
    setEditingTeam(team);
    setShowUpsertModal(true);
  };

  const closeModal = () => {
    setEditingTeam(undefined);
    setShowUpsertModal(false);
    void refetch();
  };

  const closeMembersModal = () => {
    setViewingMembersTeam(undefined);
  };

  return (
    <Permission
      resource={TEAM.resource}
      action={TEAM.actions.update}
      denied={
        <Page>
          <Card>
            <EmptyState>
              <strong>Access denied</strong>
              You do not have permission to manage teams.
            </EmptyState>
          </Card>
        </Page>
      }
    >
      <Page>
        <PageHeader>
          <div>
            <PageTitle>Teams</PageTitle>
            <PageSubtitle>Manage team structure, team owners, and membership.</PageSubtitle>
          </div>
          {canManageTeams && (
            <PrimaryButton type="button" onClick={openCreateModal}>
              Add team
            </PrimaryButton>
          )}
        </PageHeader>

        <Card>
          <CardBody>
            {loading || !data ? (
              <EmptyState>
                <Spinner />
              </EmptyState>
            ) : data.adminTeams.length === 0 ? (
              <EmptyState>
                <strong>No teams yet</strong>
                Create a team to organize owners and members.
                {canManageTeams && (
                  <div style={{ marginTop: 16 }}>
                    <PrimaryButton type="button" onClick={openCreateModal}>
                      Add team
                    </PrimaryButton>
                  </div>
                )}
              </EmptyState>
            ) : (
              <TableWrap>
                <SortBar>
                  <SortLabel>Sort by</SortLabel>
                  {SORT_KEYS.map((key) => (
                    <SortChip key={key} type="button" active={sort.key === key} onClick={() => toggleSort(key)}>
                      {teamSortLabel[key]}
                      {sortArrow(key)}
                    </SortChip>
                  ))}
                </SortBar>
                <Table>
                  <thead>
                    <tr>
                      <th className="col-team">Team</th>
                      <th className="col-owners">Team owners</th>
                      <th className="col-members">Members</th>
                      <th className="col-status">Status</th>
                      <th className="col-actions align-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTeams.map((team) => (
                      <tr key={team.id}>
                        <td className="col-team">
                          <CellContent>
                            <TeamName>{team.name}</TeamName>
                            <MetaLine>{buildTeamMeta(team)}</MetaLine>
                          </CellContent>
                        </td>
                        <td className="col-owners">
                          <CompactCellContent>
                            {team.owners.length === 0 ? (
                              <Pill tone="muted">No owners</Pill>
                            ) : (
                              <PillGroup>
                                {team.owners.map((owner) => (
                                  <Pill key={owner.id} tone="accent">
                                    {owner.name}
                                  </Pill>
                                ))}
                              </PillGroup>
                            )}
                          </CompactCellContent>
                        </td>
                        <td className="col-members">
                          <CompactCellContent>
                            <Pill tone={team.usersCount > 0 ? 'neutral' : 'muted'}>
                              {team.usersCount} {team.usersCount === 1 ? 'member' : 'members'}
                            </Pill>
                          </CompactCellContent>
                        </td>
                        <td className="col-status">
                          <CompactCellContent>
                            <Pill tone={team.assignable ? 'success' : 'muted'}>
                              {team.assignable ? 'Assignable' : 'Not assignable'}
                            </Pill>
                          </CompactCellContent>
                        </td>
                        <td className="col-actions align-right">
                          <ActionsCellContent>
                            <ActionGroup>
                              <TextButton type="button" onClick={() => setViewingMembersTeam(team)}>
                                Members
                              </TextButton>
                              {canManageTeams && (
                                <TextButton type="button" onClick={() => openEditModal(team)}>
                                  Edit
                                </TextButton>
                              )}
                            </ActionGroup>
                          </ActionsCellContent>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            )}
          </CardBody>
        </Card>
      </Page>

      {showUpsertModal && (
        <UpsertModal
          team={editingTeam}
          teamOptions={data?.adminTeams ?? []}
          regionOptions={data?.regions ?? []}
          teamTypeOptions={data?.orgTeamTypes ?? []}
          userOptions={data?.users ?? []}
          closeModal={closeModal}
        />
      )}
      {viewingMembersTeam && (
        <MembersModal
          teamId={viewingMembersTeam.id}
          teamName={viewingMembersTeam.name}
          memberCount={viewingMembersTeam.usersCount}
          onClose={closeMembersModal}
        />
      )}
    </Permission>
  );
};
